import { useContext, useState, useRef } from 'react'
import { motion, AnimatePresence } from "framer-motion"
import classnames from 'classnames/bind'
import { StoreContext } from '~/store'
import { StepperContext } from '~/components/ui/ViewStepper'
import { getOptions, formatReadableDate } from '~/utils/format'
import { getCarType, getTaxiAndEta, getOptionText, getVoucherValue, hasTaxiAndEta } from '~/utils/data'
import { formatTime } from '~/utils/format'
import { ChangeButton, CharIcon, EditField, Modal, BookingSummaryField } from '~/components'
import * as common from '~/config/common.module.css'
import * as styles from './index.module.css'
import * as MotionElement from '~/components/ui/MotionElement'
import { useTranslation } from 'react-i18next'
import i18next from '~/i18n'
import SummaryIcon from '../SummaryIcon'

const cx = classnames.bind(styles)

const formatAddress = address => {
  return address.streetNumber 
    ? `${address.streetName} ${address.streetNumber}${address.entrance ? ' ' + address.entrance : ''}`
    : address.streetName
}

const formatAvailability = (availabilities, serviceType) => {
  const availability = availabilities.find(item => serviceType === item.serviceType)
  if (availability?.expectedWaitTime === undefined) return null
  // if (availability?.availableAt === 'DELAYED') return `Endast förbokning ${formatTime(new Date(availability.pickupTime))}`
  if (availability?.availableAt === 'DELAYED') return `Endast förbokning`
  return i18next.t('info-available-in', { minutes: availability?.expectedWaitTime })
}

const renderPaymentIcon = (values) => {
  if (values.payment === 'PayInCar') {    
    return <SummaryIcon id="PayInCar"></SummaryIcon>
  }
  else {
    const isCreditCard = values.payment.indexOf('CreditCard') !== -1
    const icon = isCreditCard ? `Card${values.account.cardType}` : 'TSAB';
    return <SummaryIcon id={icon}></SummaryIcon>
  }
}

const renderPayment = (values) => {
  if (values.payment === 'PayInCar') {    
    return (
      <div className={styles.payment}>
         <div>{i18next.t('title-pay-in-car')}</div>
      </div>
    );
  }
  else {
    const isCreditCard = values.payment.indexOf('CreditCard') !== -1
    const title = isCreditCard ? values.account.maskedAccount : values.account.accountName
    const subtitle = isCreditCard ? values.account.cardType : values.account.accountNumber
    const icon = isCreditCard ? `Card${values.account.cardType}` : 'TSAB';
    return (
      <div className={styles.payment}>
          <div className={styles.ellipsis}>{title}</div>
          <div className={common.subtitle}>&nbsp;({subtitle})</div>
      </div>
    )
  }
}

const renderStops = (stops) => {
  return stops.map((item, index) => {
    return (
      <div key={index} className={styles.stop}>
        <div className={common.bold}>{formatAddress(item.address)}</div> 
        <div className={common.subtitle}>{item.address.city}</div>
      </div>
    )
  })
}

const renderPrice = (price) => {
  if (!price) return null
  if (price.finalPrice === price.fullPrice) return `${price.finalPrice}:-`
  else return <>{price.finalPrice}:- <s>{price.fullPrice}:-</s></>
}

const renderVoucherInfo = voucher => {
  return <>{i18next.t('title-voucher')} {voucher.voucherCode} - {getVoucherValue(voucher)}</>
}


export default function BookingSummary({ values, currentView, showTime = false, showHow = false, showPayment = false }) {
  const messageFieldRef = useRef()
  const phoneFieldRef = useRef()
  const nameFieldRef = useRef()
  const referenceFieldRef = useRef()
  
  const [nameIsOpen, setNameIsOpen] = useState(false);
  const [phoneIsOpen, setPhoneIsOpen] = useState(false);
  const [messageIsOpen, setMessageIsOpen] = useState(false);
  const [referenceIsOpen, setReferenceIsOpen] = useState(false);

  const isLocationView = currentView === 'locations';
  const isTimeView = currentView === 'time';
  const isHowView = currentView === 'how';
  const isConfirmView = currentView === 'confirm';
  const isDoneView = currentView === 'done';

  const toggleMessage = () => {
    setMessageIsOpen(!messageIsOpen)
    //if (optionsButtonRef.current) optionsButtonRef.current.focus()
  }

  const togglePhone = () => {
    setPhoneIsOpen(!phoneIsOpen)
    //if (optionsButtonRef.current) optionsButtonRef.current.focus()
  }

  const toggleReference = () => {
    setReferenceIsOpen(!referenceIsOpen)
    //if (optionsButtonRef.current) optionsButtonRef.current.focus()
  }

  console.log(values);
  const { t } = useTranslation()
  const { bookings, user } = useContext(StoreContext)
  const { prev } = useContext(StepperContext)

  const booking = bookings?.find(item => item.id === values.bookingId)

  const hasDeparture = values.departure?.address !== undefined
  const hasDestination = values.destination?.address !== undefined
  const hasStops = values.intermediateStopLocations?.length > 0
  const fromAddress = hasDeparture ? formatAddress(values.departure.address) : ''
  const fromCity = hasDeparture ? values.departure.address.city : ''
  const toAddress = hasDestination ? formatAddress(values.destination.address) : ''
  const toCity = hasDestination ? values.destination.address.city : ''

  const taxiAndEta = booking ? getTaxiAndEta(booking) : ''
  const time = values.autoValues.pickupTime || values.pickupTime 
    ? formatReadableDate(values.autoValues.pickupTime || values.pickupTime) 
    : t('label-i-want-to-go-now')
  // const availability = values.eta ? formatEta(values.eta, values.serviceType) : ''

  const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
  const serviceType = values.autoValues.serviceType !== null ? values.autoValues.serviceType : values.serviceType

  const availability = values.availability?.availabilities ? formatAvailability(values.availability.availabilities, serviceType) : false
  const showAvailability = values.pickupTime === '' && availability && !taxiAndEta
  const price = values?.priceEstimate?.prices?.find(p => p.serviceType === serviceType)
  const showPrice = fixedPrice && price
  const options = getOptions(values)
  const hasBooking = !isNaN(values?.bookingId)
  const company = values.isCompanyTrip &&  user?.companyConnections ? user?.companyConnections[0] : false
  const showReference = values?.company !== false || values?.account?.type === 'TSAB' ||  values?.account?.type === 'TCA';
  const needReference = company?.requireReference === true || (values?.account !== false && (values?.account?.type ==='TSAB' || values?.account?.type ==='TCA') && values?.account?.flags.includes('reference'))
  const partnerData = (values?.availability?.direct?.providerData?.providerId && (values?.availability?.direct?.providerData?.providerId !== "TSAB" || CONFIG.svea_module)) ? 
    values?.availability?.direct?.providerData : 
    (values?.availability?.prebook?.providerData?.providerId && (values?.availability?.prebook?.providerData?.providerId !== "TSAB" || CONFIG.svea_module)) ?
    values?.availability?.prebook?.providerData :
    null;

    /*
    Partner services may vary – You will be travelling with Taxi Göteborg our Svea partner. Available services may vary,
    */
  return (
    <>
      <MotionElement.div className={cx(common.content, common.contentFull, styles.alignStart)}>
      {hasBooking && <div className={styles.group}>
            
            <div className={styles.groupContent}>
              
              <div className={styles.groupContentColumn}>
                <div className={styles.groupContentMainColumn}>
                  <div className={styles.label}>{t('label-booking-number')}</div>  
                </div>
                <div className={common.title}>{values.bookingId}</div>
              </div>
            </div>
          </div>}
        <div className={cx(common.group )}>
          <div className={styles.group}>
            <div className={styles.groupIcon}>
              <SummaryIcon id="Prebooked"></SummaryIcon>
            </div>
            <div className={styles.groupContent}>
              <div className={styles.groupContentColumn}>
                <div className={styles.groupContentMainColumn}>
                  <div className={cx(common.title, common.capitalize)}>{time}</div>
                  {values.flightNumber && <div className={common.subtitle}>Flightnummer: {values.flightNumber}</div> }
                  {values.checkedInBaggage && <ul className={styles.options}>
                  <AnimatePresence>
                    <motion.li transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}>Incheckat bagage</motion.li>
                    </AnimatePresence>
                  </ul> }
                  {showAvailability && <div className={common.subtitle}>{availability}&nbsp;</div>}
                  {hasTaxiAndEta(booking) && taxiAndEta && <div className={common.subtitle}>{taxiAndEta}&nbsp;</div>}
                </div>
                {!hasBooking && !isTimeView && <ChangeButton onClick={() => prev('time')}/>}
              </div>
            </div>
          </div>
          <div className={cx(common.group, { showStops: true} )}>
            <div>
              {/* <div className={styles.label}>RESVÄG{!hasBooking && <ChangeButton onClick={() => prev('locations')}/>}</div> */}
              <div className={cx(styles.content, styles.departure, { hasStops:true })}>
                <CharIcon char="A" className={styles.charIcon} small />
                <div className={common.title}>{fromAddress}</div>
                <div className={common.subtitle}>{fromCity}</div>
              </div>
            </div>
            {hasStops && <div className={styles.stops}>{renderStops(values.intermediateStopLocations)}</div>}
            {hasDestination && <div>
              <div className={cx(styles.content, styles.destination, { hasStops:true })}>
                {<CharIcon char={'ABCDEFGHIJKLMNOPQRST'[values.intermediateStopLocations.length + 1]} className={styles.charIcon} small />}
                <div className={common.title}>{toAddress}</div>
                <div className={common.subtitle}>{toCity}</div>
              </div>
            </div>}
          </div>
        </div>
        
        {showHow && 
            <div className={common.group}>
              <div className={styles.group}>
                <div className={styles.groupIcon}>
                  <SummaryIcon id={getCarType(serviceType)?.value}></SummaryIcon>
                </div>
                <div className={styles.groupContent}>
                  <div className={styles.groupContentColumn}>
                    <div className={styles.groupContentMainColumn}>
                      <div className={common.title}>{getCarType(serviceType)?.title}</div>
                      {<ul className={styles.options}>
                        <AnimatePresence>
                          {options.map((value, index) => <motion.li key={index} transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}>{getOptionText(value)}</motion.li>)}
                        </AnimatePresence>
                      </ul>}
                    </div>
                    <div className={styles.priceEdit}>
                      <div className={styles.price}>{renderPrice(price)}</div>
                      {!hasBooking && !isHowView && <ChangeButton onClick={() => prev('how')}/>}
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          }

        {showPayment && 
          <div className={common.group}>
            <div className={styles.group}>
              <div className={styles.groupIcon}>
                {renderPaymentIcon(values)}
              </div>
              <div className={styles.groupContent}>
                <div className={styles.groupContentColumn}>
                  <div className={styles.groupContentMainColumn}>
                    <div className={cx(common.title)}>{renderPayment(values)}</div>
                    {(values.accountReference && values.accountReference.trim()!=='') && <div className={common.subtitle}>{t('placeholder-reference')}: {values.accountReference}</div>}
                    { showPayment && <>
                      {values.vouchers && <ul className={styles.options}>
                      <AnimatePresence>
                        {values.voucherObjects.map((item, index) => <motion.li key={index} transition={{duration:.15}} initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height: 0,  opacity:0 }} className={styles.option}>{renderVoucherInfo(item)}</motion.li>)}
                        </AnimatePresence>
                      </ul>}
                    </> }
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        }

          {company && 
            <div className={common.group}>
              <BookingSummaryField title={t('label-company-trip')} value={company.name} icon="CompanyTrip" changeToggle={!isHowView && !hasBooking ? () => prev('how') : null}></BookingSummaryField>
            </div>
          }

        {true && <>
          <div className={common.group}>
            <BookingSummaryField title={values.name} value={values.phone} icon="PrivateTrip" changeToggle={!hasBooking ? togglePhone : null}></BookingSummaryField>
            <Modal isOpen={phoneIsOpen} onClose={togglePhone}>
              <EditField fields={[{id:"name", title:t('placeholder-name')}, {id:"phone", title:t('placeholder-phone')}]} title={t('header-whos-travel')} innerRef={nameFieldRef} onClose={togglePhone} />
            </Modal>
          </div>

          <div className={common.group}>
            <BookingSummaryField title={t('title-message-to-driver')} value={values?.generalComment ? values.generalComment : 'Hjälp föraren att hitta dig.'} icon="Message" changeToggle={!hasBooking ? toggleMessage : null}></BookingSummaryField>
            <Modal isOpen={messageIsOpen} onClose={toggleMessage}>
              <EditField fields={[{id:"generalComment", title:t('title-message-to-driver')}]} title={t('title-message-to-driver')} innerRef={messageFieldRef} onClose={toggleMessage} />
            </Modal>
          </div>
        </> }
        {partnerData && 
          <div className={common.group}>
            <div className={styles.group}>
              <div className={styles.groupIcon}>
                <SummaryIcon id="CompanyTrip"></SummaryIcon>
              </div>
              <div className={styles.groupContent}>
                <div className={styles.groupContentColumn}>
                  <div className={styles.groupContentMainColumn}>
                    <div className={cx(common.title)}>Svea partner</div>
                      <div className={common.subtitle}>{partnerData.name}</div>
                      <div className={common.subtitle}><a href={`tel:${partnerData.phoneNumber}`}_>{partnerData.displayPhoneNumber}</a></div>
                      <div className={common.subtitle}><a href={`mailto:${partnerData.email}`}>{partnerData.email}</a></div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        
      </MotionElement.div>
    </>
  )
}
